import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Tag } from "lucide-react";
import { REGISTERS, REGISTER_BY_KEY } from "@/lib/registers";

/**
 * Small chat-header pill that lets the user tag a chat with a
 * relationship register (Work / Family / Friend / Close). Optional —
 * leaving it unset is fine; ToneMirror Layer 2/3 fall back to a global
 * "all chats" observation when no register is provided.
 *
 * Visual: minimal pill that reads "+ Context" when unset, or shows the
 * current register label when set. Tap → popover with the four options
 * + a "Clear" entry. Sage accents to match ToneGuard's palette.
 *
 * Props:
 *   value:    current register key (work|family|friend|close|null)
 *   onChange: (next: string|null) => void
 */
const RegisterSelector = ({ value = null, onChange }) => {
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    const triggerRef = useRef(null);

    // Close on Escape + click-outside (only while open).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        const onDoc = (e) => {
            if (popoverRef.current?.contains(e.target)) return;
            if (triggerRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        const raf = window.requestAnimationFrame(() => {
            document.addEventListener("mousedown", onDoc);
            document.addEventListener("touchstart", onDoc, { passive: true });
        });
        return () => {
            document.removeEventListener("keydown", onKey);
            window.cancelAnimationFrame(raf);
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("touchstart", onDoc);
        };
    }, [open]);

    const current = value && REGISTER_BY_KEY[value] ? REGISTER_BY_KEY[value] : null;

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                type="button"
                data-testid="btn-register-selector"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className={[
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1",
                    "text-[11px] font-medium transition-colors",
                    current
                        ? "bg-[#EAF1E8] text-[#3F523A] border border-[#D5E3D1] hover:bg-[#DEE9DA]"
                        : "bg-transparent text-muted-foreground hover:text-foreground border border-border hover:border-stone-300",
                ].join(" ")}
            >
                <Tag className="h-3 w-3 opacity-70" />
                <span data-testid="register-current-label">
                    {current ? current.label : "Context"}
                </span>
                <ChevronDown className="h-3 w-3 opacity-60" />
            </button>

            {open && (
                <div
                    ref={popoverRef}
                    data-testid="register-popover"
                    role="listbox"
                    className="absolute left-0 top-full mt-1.5 z-30 w-44 rounded-2xl border border-border bg-card shadow-[0_8px_28px_-12px_rgba(40,40,40,0.18)] py-1.5 tg-enter"
                >
                    <p className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                        Who is this for?
                    </p>
                    {REGISTERS.map((reg) => {
                        const selected = value === reg.key;
                        return (
                            <button
                                key={reg.key}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                data-testid={`register-option-${reg.key}`}
                                onClick={() => {
                                    onChange?.(selected ? null : reg.key);
                                    setOpen(false);
                                }}
                                className={[
                                    "w-full flex items-center gap-2 px-3 py-1.5",
                                    "text-[13px] text-left transition-colors",
                                    selected
                                        ? "text-[#3F523A] bg-[#EAF1E8]/50"
                                        : "text-foreground hover:bg-stone-50",
                                ].join(" ")}
                            >
                                <span className="flex-1">{reg.label}</span>
                                {selected && (
                                    <Check className="h-3.5 w-3.5 text-[#5E7153]" />
                                )}
                            </button>
                        );
                    })}
                    {value && (
                        <>
                            <div className="h-px bg-border/70 my-1" />
                            <button
                                type="button"
                                data-testid="register-clear"
                                onClick={() => {
                                    onChange?.(null);
                                    setOpen(false);
                                }}
                                className="w-full px-3 py-1.5 text-[12px] text-left text-muted-foreground hover:text-foreground hover:bg-stone-50 transition-colors"
                            >
                                Clear context
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default RegisterSelector;
